import { actions, inputs, overlays } from '@ecosystem/newron-design-system';
import { cloneDeep } from 'lodash';
import React, { useState } from 'react';

import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { FilterKeys, Filters, getPageTypeWithPageName, PageType, PageTypeAndName } from '@utils/pageConfig/Types';
import { useToastMessage } from '../toast-message/ToastMessage';
import * as styles from './SavedFiltersModal.module.scss';
import {
  SavePersistedFilterMutateOptions,
  useSavePersistedFilter,
} from '@utils/hook/saveFilter/UseSavePersistedFilter';
import { FilterPersistenceResponse, SaveFilterRequest } from '@utils/hook/saveFilter/Types';
import { byFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';
import {
  ELEMENT_CATEGORY_BTN,
  PAGE_ELEMENT_CLICK_TRACKING_TITLE,
  pageElementClickTrackingFunction,
  PageElementClickTrackingParam,
  SAVE_FILTER_DIALOG_BTN,
} from '@utils/page-analytics/model/PageElementClickedTracking';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { useAnalytics } from '@ecosystem/newron-client-lib';

interface SavedFiltersModalProps {
  isSaveModalOpen: boolean;
  onClose: () => void;
  toRender: JSX.Element;
}

const SavedFiltersModal: React.FC<SavedFiltersModalProps> = ({ isSaveModalOpen, onClose, toRender }) => {
  const { track } = useAnalytics();
  const [filterName, setFilterName] = useState<string>('');
  const [filterNameExist, setFilterNameExist] = useState<boolean>(false);
  const [filterNameTooLong, setFilterNameTooLong] = useState<boolean>(false);

  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const sideFiltersState = useSelector((state: any) => state.sideFilters);

  const ERROR_MESSAGE = 'Please provide a unique filter name to proceed';
  const ERROR_LONG_MESSAGE = 'Please keep your name under 64 characters';
  const MAX_NAME_LENGTH = 64;
  const pagesConfig = usePageConfig();
  const { pageName } = pagesConfig;
  const pageTypeAndName: PageTypeAndName = pagesConfig.getPageTypeAndName();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const checkForError = (name: string, nameExist: boolean) => {
    if (nameExist) {
      return ERROR_MESSAGE;
    }
    if (name.length >= MAX_NAME_LENGTH || filterNameTooLong) {
      return ERROR_LONG_MESSAGE;
    }
    return undefined;
  };

  const handleSaveFilterResponse: (data: FilterPersistenceResponse, variables: SaveFilterRequest) => void = (
    response: FilterPersistenceResponse,
    request: SaveFilterRequest,
  ) => {
    switch (response?.status) {
      case 'SUCCESS':
        showToastMessage({
          message: `${filterName} filter was saved`,
          type: 'success',
        });
        setIsSaving(false);
        onClose();
        break;
      case 'VALIDATION_ERROR':
      case 'ERROR':
        setIsSaving(false);
        onClose();
        showToastMessage({
          message: `Filter save error. Please try again or contact the administrator.`,
          type: 'error',
        });
        break;
    }
  };

  const handleSaveFilterError: (saveError: unknown, variables: SaveFilterRequest) => void = () => {
    setIsSaving(false);
    onClose();
    showToastMessage({
      message: `Filter save error. Please try again or contact the administrator.`,
      type: 'error',
    });
  };

  const savePersistedFilterOptions: SavePersistedFilterMutateOptions = {
    onSuccess: handleSaveFilterResponse,
    onError: handleSaveFilterError,
  };

  const { mutate } = useSavePersistedFilter({ name: '', values: [] });

  const SavePSFilter = (filters: Filters[], saveFilterName: string, clientId: number) => {
    const pop: SaveFilterRequest = {
      name: saveFilterName,
      values: filters,
      context: {
        itemGroup: '-',
        country: '-',
        namespace: `clientId::${clientId}`,
      },
    };

    mutate(pop, savePersistedFilterOptions);
  };

  const { showToastMessage } = useToastMessage();

  const handleElementClickTracking = () => {
    const params: PageElementClickTrackingParam = {
      sideFilters: sideFiltersState.appliedFilters,
      eventTitle: PAGE_ELEMENT_CLICK_TRACKING_TITLE,
      retailerClientState: retailerClientState,
      elementCategory: ELEMENT_CATEGORY_BTN,
      elementName: SAVE_FILTER_DIALOG_BTN,
      pageType: getPageTypeWithPageName(pageName),
      pageName: pageName,
      access: pagesConfig.access,
      track: track,
    };
    pageElementClickTrackingFunction(params);
  };

  const handleSave = async () => {
    setIsSaving(true);
    handleElementClickTracking();

    const allSavedFilters = [...pagesConfig.savedFilters];
    const copyOfAppliedFilters = [...pagesConfig.appliedFilters[pageTypeAndName.pageType].selectedFilters];
    // a hack to get a deep copy of the applied filters
    // otherwise saved filters will get updated when applied filters changes
    const appliedFilters = cloneDeep(copyOfAppliedFilters);
    const selectedPg = appliedFilters[FilterKeys.PRODUCT_GROUP].options[0].value;

    appliedFilters
      .find((e: Filters) => e.filterKey === FilterKeys.PRICEBAND)
      ?.options.forEach((e) => {
        let pop = pagesConfig.productGroupFeatureValueList
          .find((pg) => pg.productGroup === selectedPg)
          ?.sideMenuFilterDtoList.filter(byFilterKey(FilterKeys.PRICEBAND))[0].options;
        const priceBandIndex = pop?.findIndex((p) => p.value === e.value);
        //Need to specifically check for undefined because, if priceBandIndex ==0 , it equates to false
        if ((priceBandIndex || priceBandIndex === 0) && priceBandIndex !== -1) {
          e.label = priceBandIndex.toString();
          e.value = priceBandIndex.toString();
        }
      });

    const index = allSavedFilters[pageTypeAndName.pageType].savedFilters.findIndex((e) => e.name === filterName);
    if (index !== -1 || filterName.length === 0) {
      setFilterNameExist(true);
      setIsSaving(false);
    } else if (filterName.length >= MAX_NAME_LENGTH) {
      setFilterNameTooLong(true);
      setIsSaving(false);
    } else if (!isSaving) {
      const pageType = pagesConfig.pageType;

      let clientId = pageType === PageType.RI_PRODUCT ? pagesConfig.hitlistsClientId : pagesConfig.clientId;
      SavePSFilter(appliedFilters, filterName, clientId);
    }
  };
  const { Button } = actions;
  const { TextInput } = inputs;
  const { Modal } = overlays;
  return (
    <Modal isOpen={isSaveModalOpen} onClose={onClose}>
      <Modal.Header>Save filter</Modal.Header>
      <Modal.Body>
        <div className={styles['SavedFilterModal__Criteria']}>
          <div className={styles['SavedFilterModal__Title']}>Criteria</div>
          <div className={styles['SavedFilterModal__Filters']}>{toRender}</div>
        </div>
        <div data-test-id={'new-filter-name'}>
          <TextInput
            testId="input-filter-name"
            label={'Filter name'}
            placeholder="Enter filter name"
            onChange={(value) => {
              setFilterName(value.trim());
              setFilterNameExist(false);
              setFilterNameTooLong(false);
            }}
            errorMessage={checkForError(filterName, filterNameExist)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={styles['SavedFilterModal__Buttons']}>
          <Button onClick={onClose} mode="secondary" testId="cancel-button">
            Cancel
          </Button>
          <Button testId="button-save" onClick={handleSave} disabled={isSaving}>
            Save
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SavedFiltersModal;
